import { createFileRoute, redirect, useParams } from "@tanstack/react-router";
import { z } from "zod";

import { graphql } from "@repo/graphql-types";
import Gift from "@repo/icons/default/Gift";
import Left from "@repo/icons/default/Left";

import { ContactsHistoryIdAvatar } from "@/components/assist/assist-contacts-history/contacts-history-id-avatar";
import { ContactsHistoryIdDetails } from "@/components/assist/assist-contacts-history/contacts-history-id-details";
import { ContactsHistoryIdHeaderActions } from "@/components/assist/assist-contacts-history/contacts-history-id-header-actions";
import { ContactsHistoryIdHeaderPriceTrial } from "@/components/assist/assist-contacts-history/contacts-history-id-header-price-trial";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { AssistFeatures } from "@/enums/assist-features";
import { useGraphQL } from "@/hooks/use-graphql";
import { validateFeatureAccess } from "@/lib/assist-feature-guard";
import { isProUserWithActiveTrial } from "@/lib/assist-guard";
import { FIVE_MINUTES_STALE_TIME } from "@/lib/constants/dashboards-customer-overview";
import { ServicePackageEnum } from "@/lib/enums/service-package-enum";

import type { JSX } from "react";

const TrialContactsHistoryIdPageQuery = graphql(`
  query TrialContactsHistoryIdPageQuery($id: Int!, $codPacoteServicos: Int!) {
    nuntius_contato_by_pk(id: $id) {
      ...ContactsHistoryIdHeaderActionsFragment
      ...ContactsHistoryIdHeaderAvatarFragment
      ...ContactsHistoryIdDetailsFragment
    }
    ...ContactsHistoryIdHeaderPriceTrialFragment
  }
`);

const TrialContactsHistoryIdPage = (): JSX.Element => {
  const { contactId } = useParams({
    from: "/settings/assist/contacts-history/trial-period/$contactId/",
  });

  const queryResult = useGraphQL(
    TrialContactsHistoryIdPageQuery,
    {
      id: contactId,
      codPacoteServicos: ServicePackageEnum.Pro.valueOf(),
    },
    {
      staleTime: FIVE_MINUTES_STALE_TIME,
    },
  );

  const { data } = queryResult;

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" aria-label="Voltar" />
        <HeaderTitle title="Detalhes" />
      </HeaderRoot>

      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data?.nuntius_contato_by_pk ? (
            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-between items-center">
                <ContactsHistoryIdHeaderPriceTrial data={data} />
                <ContactsHistoryIdHeaderActions data={data.nuntius_contato_by_pk} />
              </div>
              <div className="flex items-center gap-2 text-secondary-500 py-3">
                <Gift size={20} className="fill-secondary-500 shrink-0" />
                <p className="text-sm font-medium">
                  Não se preocupe, esse valor é um presente da Livance para você!
                </p>
              </div>
              <ContactsHistoryIdAvatar data={data.nuntius_contato_by_pk} />

              <div className="mt-6 flex flex-col gap-6">
                <ContactsHistoryIdDetails data={data.nuntius_contato_by_pk} />
              </div>
            </div>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute(
  "/settings/assist/contacts-history/trial-period/$contactId/",
)({
  component: TrialContactsHistoryIdPage,
  parseParams: (params) => ({
    contactId: z.int().parse(Number(params.contactId)),
  }),
  beforeLoad: ({ context, location, params }) => {
    const { user, flags } = context;
    const showContactHistoryTrialItem =
      flags["habilita-historico-de-contatos-no-menu-do-assist-trial"];

    // Redireciona para o raio-x normal se o usuário não tiver trial ativo OU se a FF da tela de trial estiver desligada
    if (!isProUserWithActiveTrial(user) || !showContactHistoryTrialItem) {
      redirect({
        to: "/settings/assist/contacts-history/$contactId",
        params: { contactId: params.contactId },
        throw: true,
      });
    }

    validateFeatureAccess({
      location,
      user,
      codFuncionalidade: AssistFeatures.ContactsHistory,
    });
  },
});
